// @flow

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose, setDisplayName } from 'recompose';
import {
  injectSheet,
  BaseText,
  Header2,
  type InjectSheetProvidedProps,
  type ThemeType
} from '@catalytic/catalytic-ui';
import WebformLogo from './WebformLogo';
import notFoundUrl from '../images/not-found.svg';
import getReferrerUrl from '../utils/getReferrerUrl';
import logError from '../utils/logError';

const REF_404 = 'ref-404';

export const Style = (theme: ThemeType) => ({
  root: {
    textAlign: 'center'
  },
  logo: {
    backgroundImage: `url(${notFoundUrl})`,
    minHeight: '11rem',
    minWidth: '11rem',
    margin: '2rem 0 1rem',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center'
  },
  content: {
    width: '100%',
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    minHeight: '1rem'
  },
  description: {
    ...theme.mixins.formMarginBottom,
    color: theme.colors.slateGrey,
    textAlign: 'center',
    marginTop: '.5rem',
    [theme.breakpoints.gt.mobile.portrait]: {
      width: '33rem',
      maxWidth: '100%'
    }
  },
  or: {
    ...theme.typography.baseText,
    display: 'block',
    flex: '1',
    verticalAlign: 'bottom',
    textTransform: 'uppercase'
  }
});
type Props = InjectSheetProvidedProps & {
  intl: Object,
  displayName?: string,
  error: Error,
  params: URLSearchParams,
  hideHeader: boolean,
  src?: string
};

export const WebformNotFound = ({
  classes,
  displayName,
  error,
  params,
  hideHeader,
  src
}: Props) => {
  const referrer = params && params.get(REF_404);

  // Log error
  logError(error);

  if (referrer) {
    const url = getReferrerUrl({ key: REF_404, referrer });

    // Disable xss lint because URL interface will encode by default.
    // See `getReferrerUrl` utility function.
    // https://developer.mozilla.org/en-US/docs/Web/API/URL
    if (url) {
      window.location.href = url; // eslint-disable-line xss/no-location-href-assign
    }

    return null;
  } else {
    return (
      <div className={classes.root}>
        {!hideHeader && <WebformLogo src={src} alt={displayName} />}
        <div className={classes.content}>
          <div className={classes.logo} />
          <Header2>
            <FormattedMessage
              id="webform.404"
              defaultMessage="Oops! Page not found."
            />
          </Header2>
          <div className={classes.description}>
            <BaseText>
              <FormattedMessage
                id="webform.404.description"
                defaultMessage="There is no form available here, but don't worry—it's probably due to one of these reasons:"
              />
              <br />
              <br />
              <strong>
                <FormattedMessage
                  id="webform.404.reasonSubmitted"
                  defaultMessage="The form was already submitted"
                />
              </strong>

              <span className={classes.or}>
                <FormattedMessage id="webform.404.or" defaultMessage="or" />
              </span>

              <strong>
                <FormattedMessage
                  id="webform.404.reasonClosed"
                  defaultMessage="The form has been closed and is no longer accepting submissions"
                />
              </strong>
              <br />
              <br />
              <FormattedMessage
                id="webform.error.line3"
                defaultMessage="If you have already double-checked the URL, you probably do not need to take any further action. If you have questions, please contact the Workflow owner or reply to the email that you received."
              />
            </BaseText>
          </div>
        </div>
      </div>
    );
  }
};
WebformNotFound.displayName = 'WebformNotFound';

export default compose(
  injectIntl,
  injectSheet(Style),
  setDisplayName('WebformNotFound')
)(WebformNotFound);
